// The survey page's Steps section (redesign spec §7): one StepSection card
// per saved step, plus the draft "New step" while one is being added. The
// list-level actions — moving a step (with the split steps its score
// carries) and removing one — live here, against the saved list, so a card
// never needs the whole page to run them.

import { Button, DropdownMenu, type SectionNavItem, toast } from "@pej/kore/components";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { ApiError } from "@/api/error";
import { usePlacements } from "@/api/placements";
import { usePutSteps, useStepsSaving } from "@/api/surveys";
import type { StepKind, Survey } from "@/api/types";
import { useSectionEditController } from "@/components/section-page/section-edit";
import { KIND_LABELS } from "../step-kinds";
import { type StepsFormValues, toPutSteps, toStepsDefaults, validateSteps } from "../survey-schema";
import { SECTION } from "../survey-sections";
import { describeStepsProblem, moveStep, removalProblem, stepPrompt } from "./step-list";
import { StepSection } from "./step-section";
import { toastStepsError } from "./toast-steps-error";

/** More steps than this on a kiosk placement earns a hint, not a rule
 *  (spec §3 rule 5): kiosk guests give 15–30 seconds. */
const KIOSK_STEP_HINT_ABOVE = 3;

const ADDABLE_KINDS: StepKind[] = ["choice", "multi", "text"];

export interface StepsSectionProps {
    survey: Survey;
    onLive: (steps: StepsFormValues | null) => void;
    draft: StepKind | null;
    onDraft: (kind: StepKind | null) => void;
}

export function StepsSection({ survey, onLive, draft, onDraft }: StepsSectionProps) {
    const controller = useSectionEditController();
    const putSteps = usePutSteps(survey.workspaceId, survey.id);
    const placementsQuery = usePlacements(survey.id);
    const saved = useMemo(() => toStepsDefaults(survey.steps).steps, [survey.steps]);
    const [notice, setNotice] = useState<string | null>(null);

    const kioskHint =
        saved.length > KIOSK_STEP_HINT_ABOVE &&
        (placementsQuery.data ?? []).some((placement) => placement.mode === "kiosk");
    // An editing step's form carries the whole list; a move under it would
    // be undone by that step's next save, so moves wait for it. A move, or a
    // new draft, while a save or move is still landing would be built from
    // the old list, and saving it would put that list back.
    const stepsSaving = useStepsSaving(survey.id);
    const addBlocked = stepsSaving || controller.isAnySectionActive || draft !== null;
    const moveBlocked = stepsSaving || (controller.activeSection?.startsWith(SECTION.step("")) ?? false);

    async function move(index: number, direction: "up" | "down") {
        setNotice(null);
        const result = moveStep(saved, index, direction);
        if (!result.ok) {
            setNotice(result.message);
            return;
        }
        if (result.steps === saved) return;
        const check = validateSteps({ steps: result.steps });
        if (check) {
            setNotice(describeStepsProblem(check));
            return;
        }
        try {
            await putSteps.mutateAsync(toPutSteps({ steps: result.steps }));
        } catch (err) {
            if (err instanceof ApiError) {
                toastStepsError("Move failed", err);
                return;
            }
            throw err;
        }
        if (result.carried.length > 0) {
            const numbers = result.carried.map((carriedIndex) => carriedIndex + 1);
            toast.success(
                numbers.length === 1
                    ? `Moved with step ${numbers[0]} (split by score)`
                    : `Moved with steps ${numbers.join(" and ")} (split by score)`,
            );
        }
    }

    async function remove(index: number): Promise<string | null> {
        const problem = removalProblem(saved, index);
        if (problem) return problem;
        try {
            await putSteps.mutateAsync(toPutSteps({ steps: saved.filter((_, position) => position !== index) }));
        } catch (err) {
            if (err instanceof ApiError) {
                toastStepsError("Delete failed", err);
                return null;
            }
            throw err;
        }
        toast.success("Step deleted");
        return null;
    }

    return (
        <section
            id={SECTION.steps}
            aria-labelledby={`${SECTION.steps}-title`}
            className="flex scroll-mt-[4.5rem] flex-col gap-6"
        >
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 id={`${SECTION.steps}-title`} className="text-lg font-semibold">
                        Steps
                    </h2>
                    <p className="mt-1 text-sm text-kore-text-muted">
                        What guests are asked, in order. Each step saves on its own.
                    </p>
                </div>
                <DropdownMenu>
                    <DropdownMenu.Trigger
                        render={
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={addBlocked}
                                title={addBlocked ? "Save or cancel the current edit first." : undefined}
                            />
                        }
                    >
                        <Plus aria-hidden="true" />
                        Add step
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="end">
                        {ADDABLE_KINDS.map((kind) => (
                            <DropdownMenu.Item key={kind} onClick={() => onDraft(kind)}>
                                {KIND_LABELS[kind]}
                            </DropdownMenu.Item>
                        ))}
                    </DropdownMenu.Content>
                </DropdownMenu>
            </div>

            {notice && (
                <p role="status" className="text-sm text-kore-text-muted">
                    {notice}
                </p>
            )}

            {saved.map((step, index) => (
                <StepSection
                    key={step.stepId}
                    survey={survey}
                    stepId={step.stepId}
                    onLive={onLive}
                    onMove={moveBlocked ? undefined : (direction) => void move(index, direction)}
                    onDelete={() => remove(index)}
                />
            ))}
            {draft && (
                <StepSection
                    survey={survey}
                    stepId={null}
                    draftKind={draft}
                    onDraftDone={() => onDraft(null)}
                    onLive={onLive}
                />
            )}

            {kioskHint && (
                <p role="note" className="rounded-kore-md border border-kore-border p-3 text-sm text-kore-text-muted">
                    This survey has more than {KIOSK_STEP_HINT_ABOVE} steps and a kiosk placement. Kiosk guests give you
                    15–30 seconds — consider a shorter survey for kiosks.
                </p>
            )}
        </section>
    );
}

export function stepNavItems(
    survey: Survey,
    draft: StepKind | null,
    errors: Record<string, boolean>,
): SectionNavItem[] {
    const sorted = [...survey.steps].sort((a, b) => a.position - b.position);
    const children: SectionNavItem[] = sorted.map((step, index) => ({
        id: SECTION.step(step.id),
        label: `${index + 1} · ${stepPrompt(step)}`,
        hasError: errors[SECTION.step(step.id)],
    }));
    if (draft) children.push({ id: SECTION.newStep, label: "New step", hasError: errors[SECTION.newStep] });
    return [{ id: SECTION.steps, label: "Steps", children }];
}
